import {
  getContainerId,
  VersionCostCalculator,
} from '../../calculators/costs/VersionCostCalculator'
import {
  CostFragment,
  CreateCostInput,
  UpdateCostInput,
} from '../../generated/graphql'
import { subtractItems } from '../../util/array-utility'
import { parseLocaleDecimalToFloat } from '../../util/formatters'
import { parseDecimal } from '../../util/parsers'
import { EMPTY_VALUE } from '../common/constants'
import type { PaybackVersionValues } from './cost-tables/PaybackTableRow'
import type { CostsDialogFormValues } from './CostsDialog'

export function mapCostToFormValues(
  cost: CostFragment,
  versionId: string,
): Partial<CostsDialogFormValues> {
  return {
    projectVersionId: versionId,
    providerId: cost.providerId ?? EMPTY_VALUE,
    costType: cost.costType,
    ...(cost.recurrencePeriodInYears && {
      recurrencePeriodInYears: String(cost.recurrencePeriodInYears),
    }),
    ...(cost.occurrenceYear && {
      occurrenceYear: String(cost.occurrenceYear),
    }),
    ...(cost.endYear && {
      endYear: String(cost.endYear),
    }),
    value: String(cost.value),
    description: cost.description,
  }
}

type CostInputData = {
  projectVersionId: string
  costInput: CreateCostInput | UpdateCostInput
  providerId: string | null
}

export function mapFormValuesToInput(
  values: CostsDialogFormValues,
): CostInputData {
  const recurrencePeriodInYears = parseDecimal(values.recurrencePeriodInYears)
    ? parseDecimal(values.recurrencePeriodInYears)
    : null
  const occurrenceYear = parseDecimal(values.occurrenceYear)
    ? parseDecimal(values.occurrenceYear)
    : null
  const endYear = parseDecimal(values.endYear)
    ? parseDecimal(values.endYear)
    : null

  const costInput = {
    costType: values.costType,
    value: parseLocaleDecimalToFloat(values.value),
    description: values.description,
    recurrencePeriodInYears,
    occurrenceYear,
    endYear,
  }

  const providerId =
    values.providerId === EMPTY_VALUE ? null : values.providerId

  return {
    projectVersionId: values.projectVersionId,
    costInput,
    providerId,
  }
}

export const getCumulativeValues = (
  version: VersionCostCalculator,
  selectedContainerName: string | null,
  containerIdsToNames: Record<string, string>,
): number[] => {
  if (selectedContainerName) {
    const containerId = getContainerId(
      version,
      containerIdsToNames,
      selectedContainerName,
    )
    return containerId ? version.getAllCumulativeCostValues(containerId) : []
  }
  return version.getAllCumulativeCostValues()
}

export const getComparativePaybackPeriod = (
  firstVersion: PaybackVersionValues,
  comparedVersion: PaybackVersionValues,
): number | null => {
  if (firstVersion.buildingYear !== comparedVersion.buildingYear) {
    return null
  }

  const comparativePeriodLength =
    firstVersion.values.length > comparedVersion.values.length
      ? comparedVersion.values.length
      : firstVersion.values.length

  if (
    comparedVersion.values[comparativePeriodLength - 1] >
    firstVersion.values[comparativePeriodLength - 1]
  ) {
    return null
  }

  const comparedValues =
    comparedVersion.values.length === comparativePeriodLength
      ? comparedVersion.values
      : comparedVersion.values.slice(0, comparativePeriodLength)
  const firstValues =
    firstVersion.values.length === comparativePeriodLength
      ? firstVersion.values
      : firstVersion.values.slice(0, comparativePeriodLength)

  const subtractedValues = subtractItems(comparedValues, firstValues)
  const relativeYearOfPayback = subtractedValues.findIndex(
    (value) => value <= 0,
  )
  return relativeYearOfPayback !== -1 ? relativeYearOfPayback : null
}
